import { useEffect, useState } from "react";
import axios from "axios";
import { Headphones, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const publicClient = axios.create({ withCredentials: false });

/**
 * Tiny inline SVG sparkline of the creator's last 30 days of podcast plays.
 * Used inside the OnlyBest Stats dashboard.
 */
export default function PodcastSparkline({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/onlybest/me/podcast-stats`, { headers: getAuthHeader() })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  if (loading) {
    return <div className="h-32 rounded-2xl bg-white/5 animate-pulse" />;
  }
  if (!data || !data.has_listing) return null;

  if (!data.has_podcast) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-start gap-4 flex-wrap" data-testid="podcast-sparkline-empty">
        <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-[#FBBF24] to-[#EC4899] items-center justify-center flex-shrink-0">
          <Headphones className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold">Add your podcast — earn 8 OnlyBest points per play.</p>
          <p className="text-xs text-[#94A3B8] mt-1">Embed your podcast on your listing in Dashboard → Listing Management → Social & Embeds.</p>
        </div>
      </div>
    );
  }

  const sparkline = data.sparkline || [];
  const totals = data.totals || {};
  const maxN = Math.max(1, ...sparkline.map((d) => d.plays));

  // Build smooth SVG path
  const W = 320;
  const H = 56;
  const PAD = 4;
  const usableH = H - 2 * PAD;
  const stepX = (W - 2 * PAD) / Math.max(1, sparkline.length - 1);
  const points = sparkline.map((d, i) => {
    const x = PAD + i * stepX;
    const y = PAD + usableH - (d.plays / maxN) * usableH;
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${PAD + (sparkline.length - 1) * stepX} ${H - PAD} L ${PAD} ${H - PAD} Z`;

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#FBBF24]/5 via-[#0F172A] to-[#EC4899]/5 p-5" data-testid="podcast-sparkline-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Headphones className="w-4 h-4 text-[#FBBF24] flex-shrink-0" />
          <h3 className="text-white font-semibold text-sm sm:text-base truncate">
            Podcast plays {data.podcast_title ? <span className="text-[#94A3B8] text-xs">· {data.podcast_title}</span> : null}
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Last 30 days</div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="podcast-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#FBBF24" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#podcast-fill)" />
        <path d={linePath} fill="none" stroke="#FBBF24" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Stat label="Last 7d"  value={totals["7d"]  ?? 0} />
        <Stat label="Last 30d" value={totals["30d"] ?? 0} />
        <Stat label="All time" value={totals.all     ?? 0} highlight />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg py-2 ${highlight ? "bg-[#FBBF24]/10" : "bg-white/[0.03]"}`}>
      <p className={`text-lg sm:text-xl font-bold tabular-nums ${highlight ? "text-[#FBBF24]" : "text-white"}`}>{(value ?? 0).toLocaleString()}</p>
      <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
    </div>
  );
}

/**
 * Public top-podcasts widget — used on the Leaderboard page.
 */
export function TopPodcastsWidget({ period = "all", limit = 5 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    publicClient.get(`${API}/onlybest/podcasts/top`, { params: { period, limit }, headers: { Authorization: "" } })
      .then((r) => { if (!cancelled) setItems(r.data.top || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [period, limit]);

  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/5 to-transparent p-5" data-testid="top-podcasts-widget">
      <div className="flex items-center gap-2 mb-3">
        <Headphones className="w-4 h-4 text-[#FBBF24]" />
        <h3 className="text-white font-semibold text-sm sm:text-base">Top Podcasts on LocalBest</h3>
      </div>
      <ol className="divide-y divide-white/5">
        {items.map((p, i) => (
          <li key={p.slug} className="py-2.5 flex items-center gap-3 min-w-0" data-testid={`top-podcast-${p.slug}`}>
            <span className="text-xs font-mono text-[#64748B] w-5 text-right">{i + 1}</span>
            {(p.podcast_image || p.logo_url) ? (
              <img src={p.podcast_image || p.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-white/10 flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Headphones className="w-4 h-4 text-[#94A3B8]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{p.podcast_title || p.name}</p>
              <p className="text-[11px] text-[#64748B] truncate">{p.name} · {(p.podcast_platform || "podcast")}</p>
            </div>
            <span className="text-xs tabular-nums text-[#FBBF24] flex-shrink-0 font-semibold">{p.plays.toLocaleString()} plays</span>
            <a href={p.podcast_url} target="_blank" rel="noopener noreferrer" className="text-[#94A3B8] hover:text-white flex-shrink-0" aria-label="Open podcast">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}
